import type { MediaAsset } from '@/types/api/assets';

/** Its own drag type, so the canvas can tell files from the Assets panel or agent chat apart from other drags. */
export const MEDIA_PANEL_DRAG_MIME = 'application/x-weavy-media-panel-files';
const MEDIA_PANEL_DRAG_SOURCE_MIME = 'application/x-weavy-media-panel-source';

export function setMediaPanelDragFiles(dataTransfer: DataTransfer, files: MediaAsset[], source?: 'sidebar_drag'): void {
  dataTransfer.setData(MEDIA_PANEL_DRAG_MIME, JSON.stringify(files));
  dataTransfer.setData(MEDIA_PANEL_DRAG_SOURCE_MIME, source ?? '');
}

/** The dragged files, or undefined when this is not a media-panel drag. */
export function getMediaPanelDragFiles(dataTransfer: DataTransfer): MediaAsset[] | undefined {
  const raw = dataTransfer.getData(MEDIA_PANEL_DRAG_MIME);
  if (!raw) {
    return undefined;
  }
  try {
    const files: unknown = JSON.parse(raw);
    return Array.isArray(files) && files.length > 0 ? (files as MediaAsset[]) : undefined;
  } catch {
    return undefined;
  }
}

/** Only the Assets sidebar supplies this source; other producers retain their existing tracking. */
export function getMediaPanelDragSource(dataTransfer: DataTransfer): 'sidebar_drag' | undefined {
  const source = dataTransfer.getData(MEDIA_PANEL_DRAG_SOURCE_MIME);
  return source === 'sidebar_drag' ? source : undefined;
}

/** The size and corners of a reference tile in the generator's prompt box. */
const THUMBNAIL_DRAG_PICTURE_PX = 60;
const THUMBNAIL_DRAG_PICTURE_RADIUS_PX = 5;

/**
 * Drags a small square crop of the tile's picture, like a reference thumbnail, instead of the browser's copy of
 * the whole output. The picture is drawn from the image or video frame the tile already shows, so it is ready the
 * moment the drag starts. Without one loaded, the browser's own drag picture stays.
 */
export function setThumbnailDragPicture(dataTransfer: DataTransfer, tile: HTMLElement): void {
  const source = tile.querySelector('img, video');
  const ready =
    (source instanceof HTMLImageElement && source.complete && source.naturalWidth > 0) ||
    (source instanceof HTMLVideoElement && source.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA);
  if (!source || !ready) {
    return;
  }
  const width = source instanceof HTMLImageElement ? source.naturalWidth : source.videoWidth;
  const height = source instanceof HTMLImageElement ? source.naturalHeight : source.videoHeight;
  const scale = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');
  canvas.width = THUMBNAIL_DRAG_PICTURE_PX * scale;
  canvas.height = THUMBNAIL_DRAG_PICTURE_PX * scale;
  Object.assign(canvas.style, {
    height: `${THUMBNAIL_DRAG_PICTURE_PX}px`,
    left: '-1000px',
    position: 'fixed',
    top: '-1000px',
    width: `${THUMBNAIL_DRAG_PICTURE_PX}px`,
  });
  const context = canvas.getContext('2d');
  if (!context) {
    return;
  }
  // Crops to the middle square, the same as the thumbnail's `object-fit: cover`.
  const side = Math.min(width, height);
  context.beginPath();
  context.roundRect(0, 0, canvas.width, canvas.height, THUMBNAIL_DRAG_PICTURE_RADIUS_PX * scale);
  context.clip();
  context.drawImage(source, (width - side) / 2, (height - side) / 2, side, side, 0, 0, canvas.width, canvas.height);
  // The browser takes its picture of the element during this event, so it only has to be on the page until then.
  document.body.appendChild(canvas);
  dataTransfer.setDragImage(canvas, THUMBNAIL_DRAG_PICTURE_PX / 2, THUMBNAIL_DRAG_PICTURE_PX / 2);
  requestAnimationFrame(() => canvas.remove());
}
